import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Search,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  Car,
} from "lucide-react";
import { useListing } from "@/hooks/useListing";
import { ListingOverview } from "@/types/listing";
import { Pagination } from "@/components/ui/pagination";
import {
  getStatusColor,
  getStatusLabel,
  formatMileage,
} from "@/utils/listingUtils";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";

interface ListingSelectionStepProps {
  onListingSelect: (listing: ListingOverview) => void;
  onCancel?: () => void;
  isStandalone?: boolean;
  campaignType?: "auction" | "direct-purchase";
}

export const ListingSelectionStep = ({
  onListingSelect,
  onCancel,
  isStandalone = false,
  campaignType,
}: ListingSelectionStepProps) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const [listingSearchQuery, setListingSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [onlyWithoutCampaigns, setOnlyWithoutCampaigns] = useState(true);

  const { getListingsWithFilters } = useListing();

  const listingsQuery = getListingsWithFilters({
    page: currentPage,
    limit: itemsPerPage,
    search: activeSearchQuery || undefined,
    status: onlyWithoutCampaigns ? "without-campaign" : undefined,
    listingType: campaignType,
  });

  const listingsData = listingsQuery.data;
  const isLoadingListings = listingsQuery.isLoading;
  const listingsError = listingsQuery.error;
  const refetchListings = listingsQuery.refetch;

  const listings = listingsData?.listings || [];
  const totalCount = listingsData?.totalCount || 0;
  const totalPages = listingsData?.totalPages || 0;

  // Reset page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearchQuery]);

  // Handle search
  const handleSearch = () => {
    setActiveSearchQuery(listingSearchQuery);
    setCurrentPage(1);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setListingSearchQuery("");
    setActiveSearchQuery("");
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(language === "en" ? "en-GB" : "sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (isLoadingListings) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-primary/70">
            {L.loadingListingsStep ?? "Laddar annonser..."}
          </p>
        </div>
      </div>
    );
  }

  if (listingsError) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-red-500">
            {L.couldNotLoadListings ??
              "Kunde inte ladda annonser. Försök igen."}
          </p>
          <Button
            onClick={() => refetchListings()}
            variant="outline"
            className="mt-2"
          >
            {L.tryAgain ?? "Försök igen"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder={
                L.searchListingsStepPlaceholder ??
                "Sök efter annonser (titel, reg, vin)..."
              }
              value={listingSearchQuery}
              onChange={(e) => setListingSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={handleSearch} disabled={isLoadingListings}>
            {L.search ?? "Sök"}
          </Button>
          {activeSearchQuery && (
            <Button onClick={handleClearSearch} variant="outline">
              {L.clear ?? "Rensa"}
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="onlyWithoutCampaigns"
            checked={onlyWithoutCampaigns}
            onCheckedChange={(checked) =>
              setOnlyWithoutCampaigns(checked as boolean)
            }
          />
          <label
            htmlFor="onlyWithoutCampaigns"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {L.showOnlyListingsWithoutCampaigns ??
              "Visa endast annonser utan kampanjer"}
          </label>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {L.showingXofYListings
            ?.replace("{count}", (listings?.length || 0).toString())
            ?.replace("{total}", totalCount.toString()) ??
            `Visar ${listings?.length || 0} av ${totalCount} annonser`}
        </p>
      </div>

      {/* Listings List */}
      {!listings || listings.length === 0 ? (
        <div className="text-center py-8">
          <Car className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {L.noListingsFoundSimple ?? "Inga annonser hittades"}
          </p>
          {listingSearchQuery && (
            <p className="text-sm text-muted-foreground mt-1">
              {L.tryDifferentSearchTerm ?? "Prova att ändra söktermen"}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {listings?.map((listing) => (
            <Card
              key={listing.id}
              className="p-3 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onListingSelect(listing)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Car className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-sm">{listing.title}</h3>
                    <p className="text-xs text-muted-foreground">
                      {formatPrice(
                        listing.directPurchaseDetails?.currentPrice ||
                          listing.auctionDetails?.currentBid,
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {listing.vehicleDetails.year} •{" "}
                      {formatMileage(listing.vehicleDetails.mileage)} mil
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge
                    className={`${getStatusColor(listing.status)} text-xs`}
                  >
                    {getStatusLabel(listing.status)}
                  </Badge>
                  {listing.campaignId ? (
                    <Badge variant="outline" className="text-xs">
                      {L.hasCampaign ?? "Har kampanj"}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-xs text-green-600 border-green-600"
                    >
                      {L.available ?? "Ledig"}
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={() => {}} // Not needed for this use case
          />
        </div>
      )}

      {/* Action Buttons */}
      {isStandalone && (
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>
            {L.cancel ?? "Avbryt"}
          </Button>
        </div>
      )}
    </div>
  );
};
